/** Documents this function reads and writes. Mirrors REQUIREMENTS.md §8. */

export type Severity = 'minor' | 'moderate' | 'severe';
export type Tier = 'best' | 'good' | 'weak' | 'bad';
// §6.9 — `language` sits between compliance and financial: the words this floor
// uses, taught concretely and without the theory behind them. The theory is the
// tactics track (§6.10), which comes after certification.
export type DrillKind = 'persona' | 'objection' | 'compliance' | 'language' | 'financial'
  // §6.10 — reading whether a prospect's question is interest or an exit.
  | 'buying_signal'
  // §12.6, §12.7 — the two tactics formats. A paired comparison shows both
  // versions of one technique; a discrimination item shows one phrase and asks
  // whether the facts behind it settle the question.
  | 'paired_comparison' | 'discrimination';

export interface PersonaProfile {
  id: string;
  name: string;
  soundsLike?: string;
  tells: string[];
  primaryJob: string;
  vulnerable: boolean;
  sentiment: {
    startOffset: number;
    tierMultipliers: Record<Tier, number>;
    categoryBonus?: Record<string, number>;
  };
}

export interface OrgConfig {
  contentVersion: string;
  rubricVersion: string;
  objectionTaxonomy: string[];
  /** §6.10 — how a question from the prospect is read (§1.1: from the pack). */
  buyingSignals?: string[];
  /** §12.7 — the reads a rep may give a phrase: legitimate, corrupted, depends. */
  tacticForms?: string[];
  /** §6.10 — tracks unlocked by finishing the ladder rather than rungs on it. */
  advancedTracks?: string[];
  personas: PersonaProfile[];
  personaMode: 'off' | 'practice' | 'always';
  personaConfusionSeverity: Record<string, Severity>;
  personaSeverityPenalty: Record<Severity, number>;
  drill?: { acceptableAnswerCredit?: number; boundaryItemWeight?: number };
  meter: MeterConfig;
  /** §10.9 — how a simulated call behaves beyond its content. */
  simulation?: {
    /**
     * How long the prospect's reply is held back after a close, in
     * milliseconds. A floor that wants four seconds and one that wants seven is
     * a content change rather than a deploy.
     */
    silenceHoldMs?: number;
  };
  certification: {
    passingPersonaScore: number;
    passingClassificationScore: number;
    passingResponseScore: number;
    passingHandoffScore: number;
    passingFinancialScore: number;
    passingComplianceScore: number;
    /** §6.9 — a higher bar. The vocabulary is finite and written down. */
    passingLanguageScore?: number;
    /** §6.10 — the advanced track's bar. Never a certification gate. */
    passingTacticsScore?: number;
    complianceRecertDays: number;
    minimumCloseBand: string;
    attemptCooldownMinutes: number;
    maxSummativeAttempts: number;
    requireManagerSignoff: boolean;
  };
  stages: string[];
  /**
   * §4.1 — stages whose content is authored per sales role, so their progress
   * is tracked per role rather than once per rep. Absent means every stage is
   * shared, which is right for an org that runs a single role.
   */
  roleScopedStages?: string[];
}

/**
 * One turn of an attempt.
 *
 * §8.6 forward-compatibility: `turns[]` must tolerate an alternative shape
 * carrying `freeText` and a server-populated `aiScore` instead of a selection.
 * The scorer dispatches on turn kind, so an AI-scored turn lands in the same
 * transcript, attempt document and rollups without a schema change.
 */
export interface ChoiceTurn {
  nodeId: string;
  optionId?: string;
  classificationSelected?: string;
  personaSelected?: string;
  explainTapped?: boolean;
  msElapsed?: number;
}

export interface FreeTextTurn {
  nodeId: string;
  freeText: string;
  aiScore?: unknown;
  msElapsed?: number;
}

export type Turn = ChoiceTurn | FreeTextTurn;

export type TurnKind = 'choice' | 'free_text';

export const turnKind = (turn: Turn): TurnKind =>
  typeof (turn as FreeTextTurn).freeText === 'string' ? 'free_text' : 'choice';

export interface Attempt {
  uid: string;
  orgId: string;
  scenarioId: string;
  type: 'drill' | 'simulation';
  stage: string;
  salesRole?: string | null;
  mode: 'practice' | 'summative';
  contentVersion?: string;
  shuffleSeed?: number;
  assignedPersona?: string;
  personaSelected?: string;
  personaRevised?: string;
  startedAt?: string | null;
  submittedAt?: string | null;
  turns: Turn[];
  result?: unknown;
}

export interface DrillKeyItem {
  id: string;
  preferredId: string;
  acceptableIds?: string[];
  explain: { what: string; better?: string };
  tellRefs?: number[];
  topic?: string;
  redFlag?: boolean;
  boundary?: boolean;
  sourceDocument?: string;
  sourceVersion?: string;
}

export interface DrillKey {
  id: string;
  type: 'drill';
  drillKind: DrillKind;
  mode: 'practice' | 'summative';
  stage: string;
  contentVersion: string;
  rubricVersion: string;
  items: DrillKeyItem[];
  sourceVersion?: string;
}

export interface ItemResult {
  itemId: string;
  selected: string | null;
  preferred: string;
  correct: boolean;
  acceptable: boolean;
  credit: number;
  /** Boundary items count double: knowing what not to say matters more than
   *  knowing the definition (§12.4). */
  weight: number;
  severity: Severity | null;
  topic?: string;
  redFlag?: boolean;
  boundary?: boolean;
}

/** A compliance item answered wrongly, recorded with its topic (§6.8). */
export interface RedFlagMiss {
  itemId: string;
  topic: string;
  selected: string | null;
}

export interface DrillResult {
  drillKind: DrillKind;
  itemsScored: number;
  itemsUnanswered: number;
  deferredTurns: number;
  itemResults: ItemResult[];
  personaScore?: number;
  classificationScore?: number;
  financialScore?: number;
  complianceScore?: number;
  /** §6.9 — vocabulary substitution accuracy (§15 reporting). */
  languageScore?: number;
  /** §6.10 — advanced-track accuracy. Never a certification gate. */
  tacticsScore?: number;
  correctCount: number;
  /** Red-flag items are pass/fail gates, not weighted points (§13.1). */
  redFlagMisses: RedFlagMiss[];
  boundaryItemsScored: number;
  severeConfusionCount: number;
  confusion: Record<string, number>;
  passed: boolean;
  failureReasons: string[];
  threshold: number;
  rubricVersion: string;
  contentVersion: string;
  scoredAt: string;
}

/* ------------------------------------------------------------------ simulation */

export type BandName = 'cold' | 'cool' | 'neutral' | 'warm' | 'hot';
export type Tone = 'hostile' | 'neutral' | 'receptive';

export interface MeterConfig {
  bands: Record<BandName, [number, number]>;
  startByRole: Record<string, number>;
  negativeMultiplier: number;
  complianceViolationDelta: number;
}

/** Rep-readable half of a node (§8.4) — structure and option text only. */
export interface ScenarioNode {
  id: string;
  role?: string;
  customerLine?: string;
  personaOverrides?: Record<string, string>;
  toneVariants?: Partial<Record<Tone, string>>;
  audio?: Record<string, string>;
  requiresPersonaClassification?: boolean;
  requiresClassification?: boolean;
  /** §11.3 — the categories offered here; omit to offer the whole taxonomy. */
  classificationChoices?: string[];
  /** Personas offered on a classification node; omit for eligiblePersonas. */
  personaChoices?: string[];
  personaSensitive?: boolean;
  isPostClose?: boolean;
  /**
   * §10.9 — a close or trial close happens here, so the rep should stop talking
   * and let the prospect answer first. The client holds the reply back for a
   * real interval before revealing it.
   */
  awaitsSilence?: boolean;
  isOutcome?: boolean;
  outcomeText?: Record<string, string>;
  optionSets?: Record<string, { id: string; text: string }[]>;
  next?: string;
}

export interface ScenarioDoc {
  id: string;
  type: 'simulation';
  mode: 'practice' | 'summative';
  role: string;
  stage: string;
  variantGroup?: string;
  title: string;
  eligiblePersonas: string[];
  inheritedBand?: BandName;
  /**
   * §4.3 — how the call reaches the next person. 95% of successful setter calls
   * end in a live warm transfer; 5% are scheduled out, and the appointment is
   * then the close (§10.5). Omitted means live, because live is the norm.
   */
  handoff?: 'live' | 'scheduled';
  /**
   * What the setter said at handoff, as the closer sees it. Only the text is
   * rep-readable: whether it was accurate is the answer to the scenario, and it
   * lives in the key.
   */
  briefing?: { text: string };
  startNodeId: string;
  personaClassificationNodeId?: string;
  personaRevisionNodeId?: string;
  postClose?: string[];
  outcomeNodeId: string;
  nodes: Record<string, ScenarioNode>;
  contentVersion: string;
}

/** Answer-key half of an option (§8.5). Server-only outside practice. */
/** §21 — the two behaviors excluded from the best tier anywhere in the bank. */
export type Guardrail = 'isolates_decision_maker' | 'funding_advice';

export interface OptionKey {
  id: string;
  tier: Tier;
  points: number;
  sentimentDelta: number;
  compliance?: string;
  guardrail?: Guardrail;
  outcome?: string;
  explain: { what: string; better: string };
  /**
   * §17.2 — the technique this option uses and which form of it.
   *
   * Key-side because it is an answer: telling a rep that an option is corrupted
   * future-pacing is telling them which one not to pick. The scoring trigger
   * reads it here to build the drift report (§15.7).
   */
  tactic?: string;
  tacticForm?: 'legitimate' | 'corrupted' | 'neutral';
  /** §17.2 — a §4 vocabulary violation the option's wording carries. */
  vocabularyFlag?: string;
}

export interface ScenarioKeyNode {
  id: string;
  actualCategory?: string;
  /**
   * §6.10 — the read on each rendering of this node's line, keyed the way the
   * line is chosen: `default` for the base line, a persona id for that
   * persona's override. A rendering with no entry asked no question.
   */
  buyingSignal?: Record<string, string>;
  optionSets?: Record<string, OptionKey[]>;
}

export interface ScenarioKey {
  id: string;
  type: 'simulation';
  mode: 'practice' | 'summative';
  stage: string;
  contentVersion: string;
  rubricVersion: string;
  nodes: Record<string, ScenarioKeyNode>;
  personaResolution: { method: string; eligiblePersonas: string[] };
  outcomeNodeId: string;
  postClose: string[];
  /** §4.3 — whether the briefing the closer was handed was true. Server-only:
   *  a rep who could read it would know before the call whether to reset it. */
  briefingAccurate?: boolean;
}
